import { Check, ExternalLink, FileText, RotateCw, Star } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type { Mention } from "@/shared/types/mention";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { HighlightedText } from "@/shared/components/HighlightedText";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { SentimentBadge } from "@/shared/components/SentimentBadge";
import { classificationWhy, type WhyLine, type WhySection } from "./classification-why";
import type { PremiseFor } from "./use-premise-texts";
import { useMentionFullText } from "./use-mention-full-text";
import { GenerateSummary, SummaryText } from "./MentionSummary";
import { useMentionSummary, type MentionSummaryState } from "./use-mention-summary";
import type { MentionTextView } from "@/shared/vocabulary/mention-text-views";
import { splitMarks } from "./why-search";

export type WhyCardProps = {
  mention: Mention;
  /** Para buscar o texto completo (ao abrir o painel do texto). */
  token: string;
  /** O interruptor da página: o painel do texto da publicação abre/fecha
   *  em todos os cards. O clique no card sobrepõe até o interruptor mudar. */
  textOpen?: boolean;
  /** Como mostrar o texto no painel (preferência da pessoa, 2026-10-31). */
  textView?: MentionTextView;
  /** Pode pedir o resumo (Técnico ou acima). */
  canGenerateSummary?: boolean;
  selected: boolean;
  /** A busca dentro das justificativas: o texto casado vai em `<mark>`. */
  query?: string;
  onToggleSelect: () => void;
  /** Abre a menção no drawer. */
  onOpen: () => void;
  /** O texto das premissas de polaridade (cadastro); sem ele o selo da IA
   *  fica só com o número. */
  premiseFor?: PremiseFor;
};

/** O texto com a parte casada pela busca marcada. */
function Marked({ text, query }: { text: string; query: string }): ReactNode {
  return splitMarks(text, query).map((segment, index) =>
    segment.hit ? <mark key={index}>{segment.text}</mark> : segment.text
  );
}

/**
 * O card da página "Justificativas" (2026-10-20): uma caixa por menção em
 * que a PROTAGONISTA é a explicação. Em cima, só o contexto (quem, quando,
 * qual matéria, em letra pequena e apagada); no corpo, quatro blocos —
 * polaridade, abordagem, temas, mensagens-chave — cada linha com o VALOR
 * como chip à esquerda e, à direita, a origem (selo) e a justificativa em
 * texto de leitura.
 *
 * É visão de LEITURA: não edita nada, e montar o card não faz chamada
 * nenhuma — o "porquê" permanente já vem na própria menção
 * (`classificationWhy`, puro e testado).
 */
export function WhyCard({
  mention,
  token,
  textOpen = false,
  textView = "post",
  canGenerateSummary = false,
  selected,
  query = "",
  onToggleSelect,
  onOpen,
  premiseFor
}: WhyCardProps) {
  const platform = mentionPlatform(mention);
  const PlatformIcon = platform.Icon;
  const untitled = mention.title === "Publicação sem título";
  const context = untitled ? mention.excerpt : mention.title;
  // O painel do texto: o valor do interruptor da página, com a escolha
  // local por cima. Quando o interruptor muda, a escolha local some e
  // todos os cards voltam a seguir a página.
  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => setOverride(null), [textOpen]);
  const open = override ?? textOpen;
  // O texto completo mora AQUI (e não no painel, que desmonta ao fechar): é
  // o que faz fechar e reabrir reaproveitar a busca. Painel fechado nunca
  // busca; aberto, busca sozinho — `load()` é idempotente.
  const fullText = useMentionFullText(mention, token);
  const { load } = fullText;
  const summaryState = useMentionSummary(mention, token);
  // Com o resumo no lugar do texto, a matéria inteira só é buscada quando
  // alguém pede (o botão do painel chama `load`).
  const summaryFirst = textView === "summary" && summaryState.summary !== null;
  useEffect(() => {
    if (open && !summaryFirst) void load();
  }, [open, summaryFirst, load]);
  return (
    <article className={`why-card${selected ? " is-selected" : ""}`} aria-label={`Menção ${mention.id}`}>
      <header className="why-card-head">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Selecionar a menção ${mention.id}`}
        />
        <span
          className={`mention-row-logo net-${platform.tone}`}
          role="img"
          aria-label={platform.label}
          title={platform.label}
        >
          {hasPlatformLogo(platform.tone) ? (
            <PlatformLogo tone={platform.tone} size={14} mono />
          ) : (
            <PlatformIcon size={15} aria-hidden="true" />
          )}
        </span>
        <span className="why-card-date">{mention.date}</span>
        <span className="why-card-source" title={mention.source}>
          {mention.source || platform.label}
        </span>
        {mention.brandName && (
          <strong className="why-card-brand" title={mention.brandName}>
            {mention.brandName}
          </strong>
        )}
        {mention.isCompetitor && (
          <span className="why-card-competitor">Concorrente: {mention.companyName}</span>
        )}
        {mention.reviewedAt && (
          <span className="why-card-reviewed">
            <Check size={13} aria-hidden="true" />
            Revisada
          </span>
        )}
        <button
          type="button"
          className="why-card-text-toggle"
          aria-expanded={open}
          aria-label={`${open ? "Ocultar" : "Ver"} o texto da menção ${mention.id}`}
          onClick={() => setOverride(!open)}
        >
          <FileText size={14} aria-hidden="true" />
          {open ? "Ocultar texto" : "Ver texto"}
        </button>
        <button
          type="button"
          className="why-card-open"
          onClick={onOpen}
          aria-label={`Abrir a menção ${mention.id}`}
        >
          <ExternalLink size={14} aria-hidden="true" />
          Abrir
        </button>
      </header>

      {/* Com o painel aberto o título já está nele: a linha de contexto some. */}
      {context && !open && (
        <p className="why-card-context" title={context}>
          {context}
        </p>
      )}

      <div className={`why-card-layout${open ? " has-text" : ""}`}>
        {open && (
          <WhyPublication
            mention={mention}
            fullText={fullText}
            summaryState={summaryState}
            textView={textView}
            canGenerateSummary={canGenerateSummary}
          />
        )}
        <div className="why-card-body">
          {classificationWhy(mention, premiseFor).map(section => (
            <WhyBlock key={section.key} section={section} mention={mention} query={query} />
          ))}
        </div>
      </div>
    </article>
  );
}

/**
 * O texto da publicação, ao lado (ou acima, em card estreito) das
 * justificativas: a fonte, o título e o texto, com o termo da marca em
 * destaque (`HighlightedText`, o mesmo do card de menções). A busca nas
 * justificativas NÃO passa por aqui — o `<mark>` dela é só das
 * justificativas. O painel abre direto na matéria inteira: enquanto o
 * detalhe não chega, mostra o trecho da lista com um aviso; se falhar,
 * fica o trecho e o "Tentar de novo".
 */
function WhyPublication({
  mention,
  fullText,
  summaryState,
  textView,
  canGenerateSummary
}: {
  mention: Mention;
  fullText: ReturnType<typeof useMentionFullText>;
  summaryState: MentionSummaryState;
  textView: MentionTextView;
  canGenerateSummary: boolean;
}) {
  // Com a preferência em "resumo" e resumo na menção, ele abre no lugar do
  // trecho e o texto inteiro fica a um clique (2026-10-31).
  const wantsSummary = textView === "summary";
  const [postShown, setPostShown] = useState(false);
  const summaryOn = wantsSummary && summaryState.summary !== null && !postShown;
  const showingFull = fullText.full !== null;
  const text = fullText.full ?? mention;
  const untitled = mention.title === "Publicação sem título";
  return (
    <aside className="why-publication" aria-label={`Texto da menção ${mention.id}`}>
      {/* O miolo rola dentro do painel quando ele acompanha a altura do card (ver o CSS). */}
      <div className="why-publication-scroll">
      {mention.source && <p className="why-publication-source">{mention.source}</p>}
      {!untitled && (
        <strong className="why-publication-title">
          <HighlightedText text={mention.title} spans={mention.highlights.title} />
        </strong>
      )}
      {summaryOn ? (
        <>
          <SummaryText
            summary={summaryState.summary as string}
            origin={summaryState.origin}
            className="why-publication-text"
          />
          <button
            type="button"
            className="why-publication-full"
            onClick={() => {
              setPostShown(true);
              void fullText.load();
            }}
          >
            Ver texto completo
          </button>
        </>
      ) : (
        <p className={`why-publication-text${showingFull ? " is-full" : ""}`}>
          <HighlightedText text={text.excerpt} spans={text.highlights.content} />
        </p>
      )}
      {wantsSummary && summaryState.summary === null && canGenerateSummary && !mention.discardedAt && (
        <GenerateSummary state={summaryState} />
      )}
      {fullText.loading && (
        <p className="why-publication-status" role="status">
          Carregando o texto completo…
        </p>
      )}
      {fullText.error && (
        <div className="why-publication-failure">
          <p className="form-error" role="alert">
            {fullText.error}
          </p>
          <button type="button" className="why-publication-full" onClick={() => void fullText.load()}>
            <RotateCw size={14} aria-hidden="true" />
            Tentar de novo
          </button>
        </div>
      )}
      </div>
    </aside>
  );
}

function WhyBlock({
  section,
  mention,
  query
}: {
  section: WhySection;
  mention: Mention;
  query: string;
}) {
  return (
    <section className="why-block" data-dimension={section.key}>
      <h4 className="why-block-title">{section.title}</h4>
      <div className="why-block-rows">
        {section.lines.map(line => (
          <WhyRow key={line.key} line={line} section={section} mention={mention} query={query} />
        ))}
      </div>
    </section>
  );
}

/** O VALOR da linha como chip: a pílula de polaridade, a abordagem, o
 *  `GRUPO · Tema` (com a estrela do destaque) ou a mensagem. */
function ValueChip({ line, section, mention }: { line: WhyLine; section: WhySection; mention: Mention }) {
  if (section.key === "polarity" && line.badge !== null) {
    return <SentimentBadge value={mention.sentiment} polarity={mention.polarity} />;
  }
  return (
    <span className="why-chip">
      {line.star && (
        <Star
          size={12}
          className={`why-chip-star${line.star === "manual" ? " is-manual" : ""}`}
          aria-label={
            line.star === "manual"
              ? "Subtema em destaque (escolhido)"
              : "Subtema em destaque (sugestão automática)"
          }
        />
      )}
      {line.label}
    </span>
  );
}

function WhyRow({
  line,
  section,
  mention,
  query
}: {
  line: WhyLine;
  section: WhySection;
  mention: Mention;
  query: string;
}) {
  // Linha sem valor ("Sem polaridade", "Nenhum tema"): nada a explicar.
  if (line.badge === null) {
    return (
      <div className="why-row is-empty">
        <span className="why-empty">{line.label}</span>
      </div>
    );
  }
  return (
    <div className="why-row">
      <div className="why-row-value">
        <ValueChip line={line} section={section} mention={mention} />
      </div>
      <div className="why-row-reason">
        <span className="why-badge" data-source={line.source ?? "none"}>
          {line.badge}
        </span>
        {line.note && (
          // O texto da premissa vem do cadastro, não das colunas que o `why_q`
          // do servidor pesquisa: por isso aparece SEM marcar a busca.
          <p className="why-text why-note">{line.note}</p>
        )}
        {line.text && (
          <p className="why-text">
            <Marked text={line.text} query={query} />
          </p>
        )}
        {line.details.map((detail, index) => (
          <p key={index} className="why-text">
            <Marked text={detail} query={query} />
          </p>
        ))}
      </div>
    </div>
  );
}
